import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import LeafletMap from "./LeafletMap";
import { IoIosAlert } from "react-icons/io";
import { Link, useNavigate } from "react-router-dom";
import type { AllListings } from "../../types/allListings";
import {
    ChevronRight,
    Home,
    Images,
    Loader,
    Rocket,
    Trash2,
    Video,
} from "lucide-react";
import { useSocket } from "../../context/SocketContext";
import type { Progress } from "./types";
import type { CampaignProgress } from "../campaign/types";
import CampaignStatusCard from "../campaign/CampaignStatusCard";
import { toast } from "react-toastify";
import CustomToast from "../misc/CustomToast";
import { getAuthHeader } from "../../utils/authHeader";
import axios from "axios";
import type { CampaignCardListingProps } from "./listing/types";
import { useLocation } from "react-router-dom";
import { Button } from "../misc/Button";
import SimpleTooltip from "../misc/SimpleTooltip";
import { ListingType } from "../../types/userListing";
import { useAutopilotEnabled } from "../../hooks/useAutopilotEnabled";
import { getApiErrorMessage, getApiRefusalMessage } from "../../utils/apiError";
import {
    propertyGroupBadgeLabel,
    propertyGroupTooltip,
} from "../../utils/propertyGroupCopy";
import {
    isAutopilotManagedListing,
    isAutopilotManagedListingPending,
} from "../../utils/autopilotManagedListing";
import LoadingScreen from "../../screens/LoadingScreen";
import ProcessingStatus from "../misc/ProcessingStatus";
import PlanLimitReached from "../misc/PlanLimitReached";
import MlsReviewBanner from "./MlsReviewBanner";
import ReconfirmOverlay from "../misc/ReconfirmOverlay";

interface ListingDisplayProps {
    listings: AllListings[];
    mlsApproved: boolean;
    fetchListings: () => Promise<void>;
    listingsLimit: number;
    listingsWarn: number;
    listingsActiveLimit: number;
    reviewCount?: number;
    archivedReviewCount?: number;
    onReview?: () => void;
    hasMlsAgentId?: boolean;
    onRefreshReview?: () => void;
    reviewRefreshing?: boolean;
}

const ListingDisplay: React.FC<ListingDisplayProps> = ({
    listings,
    mlsApproved,
    fetchListings,
    listingsLimit,
    listingsWarn,
    listingsActiveLimit,
    reviewCount = 0,
    archivedReviewCount = 0,
    onReview,
    hasMlsAgentId = false,
    onRefreshReview,
    reviewRefreshing = false,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { socket, isAuthenticated } = useSocket();
    const [loading, setLoading] = useState(false);
    // MB-1436: hide manual campaign creation for listings Autopilot manages.
    const { autopilotEnabled, loading: autopilotLoading } =
        useAutopilotEnabled();

    const disabled = listingsLimit <= 0 || listingsActiveLimit <= 0 || false;

    const [currentListings, setCurrentListings] =
        useState<AllListings[]>(listings);

    const [planLimit, setPlanLimit] = useState(false);
    // Listing targeted by the card-level delete button (drives the shared confirm dialog).
    const [deleteTarget, setDeleteTarget] = useState<AllListings | null>(null);
    const subscribedListingsRef = useRef<Set<string>>(new Set());
    const subscribedCampaignsRef = useRef<Set<string>>(new Set());

    const blockedStatuses = new Set<string>([
        "Inactive",
        "Sold",
        "Error",
        "Deleted",
        "Canceled",
        "Closed",
        "Delete",
        "Expired",
        "Hold",
        "Incomplete",
        "Pending",
        "Withdrawn",
    ]);

    const progressTimeoutRef = useRef<number | null>(null);

    const stopSilenceWatcher = useCallback(() => {
        if (progressTimeoutRef.current !== null) {
            clearTimeout(progressTimeoutRef.current);
            progressTimeoutRef.current = null;
        }
    }, []);

    const startSilenceWatcher = useCallback(() => {
        stopSilenceWatcher();

        // Check if any listings are still in processing state
        const hasPending = currentListings.some(
            (l) =>
                l.status === "Processing" || l.campaignStatus === "PROCESSING"
        );

        // If no listings are pending, don't start the watcher
        if (!hasPending) return;

        // Set a timeout to check again after 5 seconds
        progressTimeoutRef.current = window.setTimeout(() => {
            fetchListings().catch(console.error);
        }, 5000);
    }, [currentListings, fetchListings, stopSilenceWatcher]);

    // 4) whenever our `listings` array changes, re-arm or teardown the watcher
    useEffect(() => {
        startSilenceWatcher();
        return stopSilenceWatcher;
    }, [currentListings, startSilenceWatcher, stopSilenceWatcher]);

    useEffect(() => {
        setCurrentListings(listings);
    }, [listings]);

    // Room subscriptions are INCREMENTAL and must survive a listings refetch.
    // Tearing them down on every `listings` change (as two duplicated effects
    // here used to) left a window with no campaign rooms joined — harmless for
    // a single manual campaign, but autopilot creates a burst of campaigns and
    // each campaign:created triggers a refetch, so progressUpdate events landing
    // mid-churn were dropped. Unsubscribe only when the socket goes away.
    useEffect(() => {
        if (!socket) return;
        const subscribedListings = subscribedListingsRef.current;
        const subscribedCampaigns = subscribedCampaignsRef.current;
        return () => {
            subscribedListings.forEach((listingId) => {
                socket.emit("unsubscribeFromListing", listingId);
            });
            subscribedListings.clear();
            subscribedCampaigns.forEach((campaignId) => {
                socket.emit("unsubscribeFromCampaign", campaignId);
            });
            subscribedCampaigns.clear();
        };
    }, [socket, isAuthenticated]);

    useEffect(() => {
        if (!socket || !isAuthenticated) return;

        // Function to subscribe to a listing
        const subscribeToListing = (listingId: string) => {
            if (!subscribedListingsRef.current.has(listingId)) {
                socket.emit("subscribeToListing", listingId);
                subscribedListingsRef.current.add(listingId);
            }
        };

        // Function to subscribe to a campaign
        const subscribeToCampaign = (campaignId: string) => {
            if (!subscribedCampaignsRef.current.has(campaignId)) {
                socket.emit("subscribeToCampaign", campaignId);
                subscribedCampaignsRef.current.add(campaignId);
            }
        };

        // Subscribe to any new listings and campaigns
        listings.forEach((listing) => {
            if (listing.status === "Processing") {
                subscribeToListing(listing.id);
            }

            if (listing.campaignId && listing.campaignStatus === "PROCESSING") {
                subscribeToCampaign(listing.campaignId);
            }
        });
    }, [socket, isAuthenticated, listings]);

    useEffect(() => {
        if (!socket) return;

        const handleProgressUpdate = (data: {
            entityId: string;
            entityType: "listing" | "campaign";
            progress: Progress | CampaignProgress;
        }) => {
            const { entityId, entityType, progress } = data;

            // Check if this update indicates completion
            const isCompleted =
                (entityType === "listing" &&
                    (progress as Progress).currentStep === "Completed") ||
                (entityType === "campaign" &&
                    (progress as CampaignProgress).currentStep === "Completed");
            
             const storageLimitReached =
                entityType === "listing" &&
                (progress as Progress).currentStep === "Storage Quota Exceeded";

            if (storageLimitReached) {
                setPlanLimit(true);
                fetchListings().catch(console.error);
            }

            // Update the listings state
            setCurrentListings((prevListings) => {
                const updatedListings = prevListings.map((listing) => {
                    if (entityType === "listing" && listing.id === entityId) {
                        return {
                            ...listing,
                            progress: progress as Progress,
                            status:
                                (progress as Progress).currentStep ===
                                "Completed"
                                    ? "Active"
                                    : listing.status,
                        };
                    } else if (
                        entityType === "campaign" &&
                        listing.campaignId === entityId
                    ) {
                        return {
                            ...listing,
                            campaignProgress: progress as CampaignProgress,
                            campaignStatus:
                                (progress as CampaignProgress).currentStep ===
                                "Completed"
                                    ? "ACTIVE"
                                    : listing.campaignStatus,
                        };
                    }
                    return listing;
                });

                return updatedListings;
            });

            // If a listing or campaign completed, fetch fresh data once
            if (isCompleted) {
                // Use setTimeout to ensure state is updated first
                setTimeout(() => {
                    fetchListings().catch(console.error);
                }, 1000);
            } else {
                // Only start the silence watcher if we're still processing
                startSilenceWatcher();
            }
        };

        socket.on("progressUpdate", handleProgressUpdate);

        // Cleanup on unmount or socket change
        return () => {
            socket.off("progressUpdate", handleProgressUpdate);
            stopSilenceWatcher();
        };
    }, [socket, fetchListings, startSilenceWatcher, stopSilenceWatcher]);

    useEffect(() => {
    if (!socket || !isAuthenticated) return;

    const handleListingDeleted = (eventData: any) => {
        if (eventData.resourceType === "listing" && eventData.action === "deleted") {
            fetchListings().catch(console.error);
        }
    };

    const handleListingUpdated = (eventData: any) => {
      if (
        eventData.resourceType === "listing" &&
        eventData.action === "updated"
      ) {

        fetchListings().catch(console.error);
      }
    };

      const handlePostUpdated = (eventData: any) => {
            if (eventData.resourceType === "post" && eventData.action === "updated") {
                fetchListings().catch(console.error);
            }
        };

         const handlePostDeleted = (eventData: any) => {
            // Check if it's a post deletion event
            if (eventData.resourceType === "post" && eventData.action === "deleted") {
                fetchListings().catch(console.error);
            }
        };

        // Autopilot creates campaigns server-side (ingest hook / daily sweep), so
        // no browser call precedes them; refetching here picks up the Processing
        // campaign and the subscribe effect then joins its progress room.
        const handleCampaignCreated = (eventData: any) => {
            if (eventData.resourceType === "campaign" && eventData.action === "created") {
                fetchListings().catch(console.error);
            }
        };

    socket.on("listing:deleted", handleListingDeleted);
    socket.on("listing:updated", handleListingUpdated);
    socket.on("post:updated", handlePostUpdated);
    socket.on("post:deleted", handlePostDeleted);
    socket.on("campaign:created", handleCampaignCreated);
    return () => {
        socket.off("listing:deleted", handleListingDeleted);
        socket.off("listing:updated", handleListingUpdated);
        socket.off("post:updated", handlePostUpdated);
        socket.off("post:deleted", handlePostDeleted);
        socket.off("campaign:created", handleCampaignCreated);
    };
}, [socket, isAuthenticated, fetchListings]);

    const viewListingHandler = (id: string) => {
        navigate(`/listing?id=${id}`);
    };

    const createListingHandler = () => {
        navigate("/create-listing");
    };

    const handleCampaignRetry = async (campaignId: string | undefined) => {
        if (!campaignId) return;
        setLoading(true);
        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        try {
            const response = await axios.get(
                `${backendUrl}/listing/restart-campaign/${campaignId}`,
                { headers: headers }
            );

            if (response.status === 200) {
                toast.success(
                    <CustomToast
                        message="Campaign generation process restarted."
                        type="success"
                    />,
                    {
                        autoClose: 3000,
                    }
                );
            }

            navigate(`/listing?id=${response.data.id}`);
        } catch (error: unknown) {
            console.error(error);
            toast.error(
                <CustomToast
                    message={getApiErrorMessage(
                        error,
                        "Failed to retry campaign."
                    )}
                    type="error"
                />,
                {
                    autoClose: 3000,
                }
            );
        } finally {
            setLoading(false);
        }
    };

    const handleListingRetry = async (
        listingId: string,
        listingType: ListingType
    ) => {
        if (listingType !== ListingType.MLSListing) {
            toast.error(
                <CustomToast
                    message="Retrying Non MLS listings is not supported. Please contact support."
                    type="error"
                />,
                {
                    autoClose: 3000,
                }
            );
            return;
        }

        setLoading(true);
        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        try {
            const response = await axios.post(
                `${backendUrl}/listing/retry`,
                {
                    listingId: listingId,
                },

                { headers: headers }
            );

            if (response.status === 201) {
                toast.success(
                    <CustomToast
                        message="Listing retry process started."
                        type="success"
                    />,
                    {
                        autoClose: 3000,
                    }
                );
            } else {
                throw new Error("Failed to retry listing");
            }
        } catch (error: unknown) {
            console.error(error);
            // The API refuses a retry it cannot complete and keeps the listing,
            // so its reason is the useful thing to show.
            toast.error(
                <CustomToast
                    message={getApiRefusalMessage(
                        error,
                        "Failed to retry listing."
                    )}
                    type="error"
                />,
                {
                    autoClose: 5000,
                }
            );
        } finally {
            setLoading(false);
            // Fetch fresh listings after retry
            fetchListings().catch(console.error);
        }
    };

    // Card-level delete — mirrors the listing detail page's "Delete Listing" action
    // (POST /listing/delete + the same confirm copy). The card only renders the delete button
    // when the listing is deletable (see the gate at render), so the same conditions apply here.
    const handleCardDelete = async () => {
        const target = deleteTarget;
        if (!target) return;
        // Close the confirm dialog immediately on click, THEN show the loader for the request.
        // ReconfirmOverlay is controlled purely by `isOpen` — its confirm button does not
        // self-close — so clearing deleteTarget only after the await left the dialog sitting
        // open under the loader. These two setState calls batch into one render: dialog closes
        // and the loader appears together.
        setDeleteTarget(null);
        setLoading(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.post(
                `${backendUrl}/listing/delete`,
                {
                    listingId: target.id,
                    listingType: target.listingType,
                },
                { headers }
            );
            if (response.status === 200) {
                toast.success(
                    <CustomToast
                        message="Listing deleted successfully."
                        type="success"
                    />,
                    { autoClose: 3000 }
                );
                await fetchListings().catch(console.error);
            } else {
                throw new Error("Failed to delete listing.");
            }
        } catch (error: unknown) {
            console.error(error);
            toast.error(
                <CustomToast
                    message="Failed to delete listing. Please try again."
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mx-auto h-full px-4 py-8">
            {loading && <LoadingScreen />}
             {planLimit && (
                <PlanLimitReached
                    open={planLimit}
                    onClose={() => setPlanLimit(false)}
                    type="listingStorage"
                />
            )}
            {/* Header Section — title and Add Listing stay on the same top row (title
                left, button right) at every width. The review banner drops to its own
                full-width row below on mobile and sits centered between them on desktop
                (>=900px) via flex order. */}
            <div className="mb-8 flex flex-wrap items-center gap-4">
                <div className="order-1 flex-1">
                    <h1 className="text-2xl font-bold text-primary">
                        My Listings
                    </h1>
                </div>
                {/* Wrap the Button with Tooltip only if it's disabled and needs a tooltip */}
                <div className="order-2 flex min-[900px]:order-3 min-[900px]:flex-1 min-[900px]:justify-end">
                    <SimpleTooltip
                        message="Please upgrade your plan to add more listings"
                        visible={
                            (listingsLimit <= 0 || listingsActiveLimit <= 0) &&
                            disabled
                        }
                    >
                        <Button
                            variant="secondary"
                            id="add-listing-btn"
                            onClick={createListingHandler}
                            disabled={disabled}
                        >
                            Add Listing
                        </Button>
                    </SimpleTooltip>
                </div>
                {mlsApproved && hasMlsAgentId && onReview && onRefreshReview && (
                    <div className="order-3 flex w-full justify-center min-[900px]:order-2 min-[900px]:w-auto">
                        <MlsReviewBanner
                            pendingCount={reviewCount}
                            archivedCount={archivedReviewCount}
                            onReview={onReview}
                            onRefresh={onRefreshReview}
                            refreshing={reviewRefreshing}
                        />
                    </div>
                )}
                {/* MB-1546: "Auto Pilot is on" chip. Own full-width row below the
                    banner and the button at every width (order-4). Hidden while the
                    flag is loading so it never shows and then gets pulled away. */}
                {autopilotEnabled === true && !autopilotLoading && (
                    <div className="order-4 flex w-full justify-center">
                        <Link
                            to="/settings/autopilot"
                            id="autopilot-on-chip"
                            className="inline-flex items-center gap-2 rounded-full border border-secondary bg-white px-4 py-1.5 text-sm font-semibold text-primary shadow-sm transition-shadow hover:border-[#DBA507] hover:shadow-md"
                        >
                            <Rocket className="h-4 w-4 text-[#DBA507]" />
                            Auto Pilot is on
                            <ChevronRight className="h-4 w-4 text-gray-500" />
                        </Link>
                    </div>
                )}
            </div>
            {/* {listingsWarn <= 0 ? (
                <div className="bg-gray-100 p-3 rounded-md my-4">
                    <p className="text-sm text-red-500">
                        <strong className="text-primary">Note:</strong> You have
                        reached the maximum number of listings allowed under
                        your current plan. To continue creating new listings
                        without interruption, please consider upgrading your
                        plan.
                    </p>
                </div>
            ) : null} */}
            {/* Listings Grid */}
            <div className="flex flex-wrap justify-center gap-6">
                {currentListings.map((listing, idx) => {
                    // Define CampaignCardListing inside the map function
                    const CampaignCardListing: CampaignCardListingProps = {
                        campaignProgress: listing?.campaignProgress,
                        campaignStatus: listing?.campaignStatus,
                        campaignType: listing?.campaignType,
                        generatedPosts: listing?.generatedPosts,
                        publishedPosts: listing?.publishedPosts,
                        scheduledPosts: listing?.scheduledPosts,
                        unapprovedPosts: listing?.unapprovedPosts,
                        contentRefresh: listing?.contentRefresh,
                        generatingVideoCount: listing?.generatingVideoCount,
                        autoVideoPostsTotal: listing?.autoVideoPostsTotal,
                        autoVideoPostsDone: listing?.autoVideoPostsDone,
                    };

                    // Same delete gate as the listing detail page: a listing that is still
                    // processing or MLS-syncing can't be deleted, and a campaign that is
                    // generating blocks delete (surfaced as a disabled button with a tooltip).
                    const canDeleteListing =
                        listing.status !== "Processing" && !listing.isSyncing;
                    const deleteBlockedByCampaign =
                        listing.campaignStatus === "PROCESSING";

                    return (
                        <div
                            id={`listing-${idx}`}
                            key={listing.id}
                            className="w-full sm:w-1/2 lg:w-1/3 max-w-sm bg-white rounded-lg shadow-2xl flex flex-col relative border-b-4"
                            style={{ borderColor: listing.color }}
                        >
                            {blockedStatuses.has(listing.status || "") && (
                                <div className="absolute top-0 bg-gray-500 bg-opacity-50 w-full h-full z-10 rounded-lg" />
                            )}

                            {/* Delete — same availability as the detail page's Delete Listing
                                button, so the user can remove a listing without opening it.
                                On-brand navy badge over the photo, destructive-red on hover.
                                Sits above the blocked-status overlay (z-30). */}
                            {canDeleteListing && (
                                <SimpleTooltip
                                    message="Please wait for the campaign to finish before deleting the listing"
                                    visible={deleteBlockedByCampaign}
                                >
                                    <button
                                        type="button"
                                        id={`delete-listing-${idx}`}
                                        aria-label="Delete listing"
                                        title="Delete listing"
                                        onClick={() => {
                                            if (!deleteBlockedByCampaign) {
                                                setDeleteTarget(listing);
                                            }
                                        }}
                                        disabled={deleteBlockedByCampaign}
                                        className="absolute top-3 right-3 z-30 flex h-8 w-8 items-center justify-center rounded-full bg-[#193456]/80 text-white shadow-md backdrop-blur-sm transition-colors hover:bg-[#d50000] focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <Trash2 className="h-[18px] w-[18px]" />
                                    </button>
                                </SimpleTooltip>
                            )}

                            {/* MB-1546: Auto Pilot pill, top-left over the photo. Reads the
                                campaign flag only - the field is only ever true on a campaign
                                Auto Pilot owns - so the card and the listing screen agree. */}
                            {listing.managedByAutopilot === true && (
                                <div
                                    id={`autopilot-pill-${idx}`}
                                    title="Auto Pilot creates and schedules this listing's posts."
                                    className="absolute top-3 left-3 z-30 flex items-center gap-1.5 rounded-full bg-primary/80 py-1 pl-2 pr-2.5 text-xs font-bold text-white shadow-md backdrop-blur-sm"
                                >
                                    <Rocket className="h-3.5 w-3.5" />
                                    Auto Pilot
                                </div>
                            )}

                            {/* Image/Map Section */}
                            <div className="relative w-full h-48 z-0">
                                {listing.primaryImage &&
                                listing.primaryImage.length > 0 ? (
                                    <img
                                        src={listing.primaryImage}
                                        alt={listing.formattedAddress}
                                        className="w-full h-full object-cover rounded-t-lg"
                                    />
                                ) : listing.latitude && listing.longitude ? (
                                    <LeafletMap
                                        latitude={listing.latitude}
                                        longitude={listing.longitude}
                                        address={listing.formattedAddress}
                                    />
                                ) : (
                                    <div className="w-full h-full bg-primary rounded-t-lg flex flex-col items-center justify-center gap-3">
                                        <Home className="h-12 w-12 text-white/60 animate-skeleton" />
                                        <span className="text-xs font-semibold tracking-wide text-secondary">
                                            {listing.listingType ===
                                            ListingType.MLSListing
                                                ? "Syncing from MLS..."
                                                : "Preparing listing..."}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Content Section */}
                            <div className="p-4 flex flex-col flex-grow">
                                {/* Address Section */}
                                <h2
                                    className="text-lg font-bold text-primary text-center mb-4 line-clamp-2 h-14 overflow-hidden"
                                    title={listing.formattedAddress}
                                >
                                    {listing.formattedAddress}
                                </h2>

                                {/* Details Section */}
                                <dl
                                    className={`space-y-2 text-sm flex flex-col flex-grow ${
                                        listing.status === "Processing" ||
                                        listing.campaignStatus === "PROCESSING"
                                            ? "justify-between"
                                            : "justify-start"
                                    }`}
                                >
                                    {/* Listing Status */}
                                    <div className="flex items-center justify-between">
                                        <dt className="flex items-center font-medium text-accent">
                                            <IoIosAlert className="mr-2 h-5 w-5 text-secondary" />
                                            Listing Status
                                        </dt>
                                        <dd className="font-medium text-primary">
                                            {listing.status
                                                ?.replace(/([A-Z])/g, " $1")
                                                .trim()}
                                        </dd>
                                    </div>
                                    {listing.propertyGroup && (
                                        <div className="flex items-center justify-between">
                                            <dt className="flex items-center font-medium text-accent">
                                                <IoIosAlert className="mr-2 h-5 w-5 text-secondary" />
                                                Property
                                            </dt>
                                            <dd>
                                                <SimpleTooltip
                                                    message={propertyGroupTooltip(
                                                        listing.propertyGroup,
                                                        autopilotEnabled
                                                    )}
                                                    visible
                                                    tooltipClassName="bg-secondary text-primary"
                                                >
                                                    <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-primary">
                                                        {propertyGroupBadgeLabel(
                                                            listing.propertyGroup
                                                        )}
                                                    </span>
                                                </SimpleTooltip>
                                            </dd>
                                        </div>
                                    )}
                                    {/* Listing Creation Updates */}
                                    {listing.status === "Processing" ? (
                                        <>
                                            <div className="flex items-center justify-center mt-4">
                                                <dt className="flex items-center justify-center font-medium text-accent">
                                                    <Loader className="mr-2 h-5 w-5 animate-spin text-secondary" />
                                                    {listing.progress ? (
                                                        <ProcessingStatus
                                                            progress={
                                                                listing.progress
                                                            }
                                                        />
                                                    ) : (
                                                        <span>
                                                            Queued for sync
                                                        </span>
                                                    )}
                                                </dt>
                                            </div>
                                            {listing.progress?.totalImages
                                                ? listing.progress
                                                      ?.totalImages > 0 && (
                                                      <div className="flex items-center justify-between">
                                                          <dt className="flex items-center font-medium text-accent">
                                                              <Images className="mr-2 h-5 w-5 text-secondary" />
                                                              Images Processed:
                                                          </dt>
                                                          <dd className="font-medium text-primary">
                                                              {
                                                                  listing
                                                                      .progress
                                                                      ?.imagesProcessed
                                                              }
                                                              /
                                                              {
                                                                  listing
                                                                      .progress
                                                                      ?.totalImages
                                                              }
                                                          </dd>
                                                      </div>
                                                  )
                                                : null}
                                            {listing.progress?.totalVideos &&
                                            listing.progress?.totalVideos >
                                                0 ? (
                                                <div className="flex items-center justify-between">
                                                    <dt className="flex items-center font-medium text-accent">
                                                        <Video className="mr-2 h-5 w-5 text-secondary" />
                                                        Videos Processed:
                                                    </dt>
                                                    <dd className="font-medium text-primary">
                                                        {
                                                            listing.progress
                                                                ?.videosProcessed
                                                        }
                                                        /
                                                        {
                                                            listing.progress
                                                                ?.totalVideos
                                                        }
                                                    </dd>
                                                </div>
                                            ) : null}
                                        </>
                                    ) : (
                                        <CampaignStatusCard
                                            listing={CampaignCardListing}
                                        />
                                    )}
                                </dl>
                            </div>
                            {/* Button Section */}
                            {listing.status !== "Processing" && (
                                <div className="p-4 flex flex-col space-y-2">
                                    {!listing.campaignStatus &&
                                        listing.status !== "Error" &&
                                        (listing.status === "Active" ||
                                            listing.status === "ComingSoon" ||
                                            listing.status ===
                                                "ActiveUnderContract") &&
                                        listing.imageCount > 0 &&
                                        !isAutopilotManagedListing(
                                            autopilotEnabled,
                                            listing,
                                        ) &&
                                        !isAutopilotManagedListingPending(
                                            autopilotLoading,
                                            listing,
                                        ) && (
                                            <Button
                                                id={`create-campaign-${idx}`}
                                                onClick={() => {
                                                    if (
                                                        listing.imageCount <= 0
                                                    ) {
                                                        toast.error(
                                                            <CustomToast
                                                                message="Please upload images before creating a campaign."
                                                                type="error"
                                                            />,
                                                            {
                                                                autoClose: 3000,
                                                            }
                                                        );
                                                    } else {
                                                        navigate(
                                                            "/create-campaign",
                                                            {
                                                                state: {
                                                                    listingId:
                                                                        listing.id,
                                                                    listingType:
                                                                        listing.listingType ===
                                                                        ListingType.MLSListing
                                                                            ? "MLSListing"
                                                                            : "Listing",
                                                                    from: location.pathname,
                                                                    parsedFooter:
                                                                        listing.parsedFooter,
                                                                    parsedGoogleFooter:
                                                                        listing.parsedGoogleFooter,
                                                                },
                                                            }
                                                        );
                                                    }
                                                }}
                                                disabled={
                                                    listing?.isSyncing || false
                                                }
                                            >
                                                Create Campaign
                                            </Button>
                                        )}
                                    {listing.campaignStatus &&
                                        listing.campaignStatus === "ERROR" && (
                                            <Button
                                                id={`retry-campaign-${idx}`}
                                                onClick={() => {
                                                    handleCampaignRetry(
                                                        listing.campaignId
                                                    );
                                                }}
                                            >
                                                Retry Campaign
                                            </Button>
                                        )}
                                    {listing.campaignStatus &&
                                        listing.campaignStatus ===
                                            "SOCIAL_PAUSE" && (
                                            <span className="text-center text-red-500">
                                                Connect Social Media platform(s)
                                                from Settings to resume campaign
                                            </span>
                                        )}

                                    {listing.status === "Error" ? (
                                        <Button
                                            id={`retry-listing-${idx}`}
                                            className="z-30"
                                            onClick={() => {
                                                handleListingRetry(
                                                    listing.id,
                                                    listing.listingType
                                                );
                                            }}
                                        >
                                            Retry Listing
                                        </Button>
                                    ) : (
                                        <Button
                                            id={`view-listing-${idx}`}
                                            variant="secondary"
                                            onClick={() => {
                                                viewListingHandler(listing.id);
                                            }}
                                            className="z-30"
                                        >
                                            View Listing
                                        </Button>
                                    )}
                                </div>
                            )}
                            {listing.isSyncing && (
                                <span className="mx-2 px-4 py-2 rounded text-primary text-sm text-center">
                                    Listing is undergoing updates
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
            <ReconfirmOverlay
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleCardDelete}
                title="Delete Listing"
                message="Are you sure you want to delete this listing?"
                messageTwo="This action cannot be undone."
                confirmText="Delete Listing"
                cancelText="Cancel"
                reverse={true}
            />
        </div>
    );
};

export default ListingDisplay;
